/**
 * sl-728o.7.1: Tutorial Launcher
 *
 * Handles the 'tutorial-card-start' custom event from TutorialCard GfxBlocks.
 * Performs RBAC check, external prerequisite check, and starts the session
 * via TutorialRegistry.
 */

import { notify } from '@affine/component';
import { Service } from '@toeverything/infra';

import type { TutorialRegistry } from './tutorial-registry';

interface TutorialCardStartDetail {
  sessionId: string;
  requiredRole: string;
  blockId: string;
}

/**
 * Provides getRoleChecker/setRoleChecker so the React layer can inject
 * the actual workspace permission check without coupling to Lit blocks.
 */
type RoleChecker = (requiredRole: string) => boolean;

export class TutorialLauncher extends Service {
  private roleChecker: RoleChecker | null = null;

  constructor(private readonly registry: TutorialRegistry) {
    super();
  }

  /**
   * Set the role checker function (called from React layer with workspace permission context).
   */
  setRoleChecker(checker: RoleChecker): void {
    this.roleChecker = checker;
  }

  /**
   * Handle a tutorial start request. Called from the custom event listener.
   */
  handleStart(detail: TutorialCardStartDetail): void {
    const { sessionId, requiredRole } = detail;

    // 1. RBAC check
    if (requiredRole && this.roleChecker && !this.roleChecker(requiredRole)) {
      notify({
        title: 'Permission required',
        message: 'You need Workspace Admin permissions to start this tutorial.',
        theme: 'warning',
      });
      return;
    }

    // 2. Get session from registry
    const session = this.registry.get(sessionId);
    if (!session) {
      notify({
        title: 'Tutorial not found',
        message: `Tutorial "${sessionId}" is not registered yet.`,
        theme: 'error',
      });
      return;
    }

    // 3. External prerequisite check
    if (session.externalPrerequisite && !session.externalPrerequisite()) {
      notify({
        title: 'Prerequisite not met',
        message:
          session.externalPrerequisiteMessage ||
          'Please complete the required setup first.',
        theme: 'warning',
      });
      return;
    }

    // 4. Start session
    const started = this.registry.start(sessionId);
    if (!started) {
      notify({
        title: 'Cannot start tutorial',
        message: 'A prerequisite tutorial must be completed first.',
        theme: 'warning',
      });
    }
  }
}
